import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { EmployeeDependantsModalComponent } from '../../components/employee-dependants-modal/employee-dependants-modal.component';
import { EmployeeFormComponent } from '../../components/employee-form/employee-form.component';
import { EmployeeHistoryComponent } from '../../components/employee-history/employee-history.component';
import { EmployeeInterventionsPanelComponent } from '../../components/employee-interventions-panel/employee-interventions-panel.component';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@shared/enums/permission.enum';
import { EmployeeResponse, UpdateEmployeeRequest } from '../../employee.model';
import { ClientStore } from '@features/client/client.store';
import { EmployeeStore } from '../../employee.store';
import { LookupStore } from '@core/lookup/lookup.store';import { EMPLOYEE_LOOKUP_CODES } from '@core/lookup/lookup-codes';

import {SHARED_IMPORTS} from "../../../../shared/modules/shared-imports.module";

@Component({
  selector: 'app-employee-detail-page',
  imports: [
    RouterLink,
    EmployeeFormComponent,
    EmployeeHistoryComponent,
    EmployeeInterventionsPanelComponent,
    ...SharedMaterialUIModule,
    ...SHARED_IMPORTS,
  ],
  templateUrl: './employee-detail.page.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EmployeeDetailPage {
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);

  protected readonly clientStore = inject(ClientStore);
  protected readonly employeeStore = inject(EmployeeStore);
  protected readonly lookupStore = inject(LookupStore);

  private readonly permissionService = inject(PermissionService);

  private selectedClientId: string | null = null;

  constructor() {
    const employeeId = this.route.snapshot.paramMap.get('employeeId');
    const clientId = this.route.snapshot.queryParamMap.get('clientId');

    this.lookupStore.load(EMPLOYEE_LOOKUP_CODES);
    this.clientStore.loadClients({ includeInactive: false });
    this.clientStore.selectClient(clientId);

    this.employeeStore.loadCompanies(clientId);
    this.employeeStore.loadManagerLookupResponses();

    this.selectedClientId = clientId;

    if (!employeeId) {
      return;
    }

    this.employeeStore.loadEmployee(employeeId);
    this.employeeStore.loadEmployeeHistory(employeeId);

    effect(() => {
      const employee = this.employeeStore.selectedEmployee();

      if (!employee) {
        return;
      }

      if (employee.clientId && employee.clientId !== this.selectedClientId) {
        this.selectedClientId = employee.clientId;
        this.clientStore.selectClient(employee.clientId);
        this.employeeStore.loadCompanies(employee.clientId);
      }

      this.clientStore.filterClientCompanies(this.selectedClientId, employee.companyId);
      this.employeeStore.loadCompanyDetails(employee.companyId);
    });
  }

  protected canUpdate(): boolean {
    return this.permissionService.hasPermission(Permission.EmployeesUpdate);
  }

  protected canViewInterventions(): boolean {
    return this.permissionService.hasPermission(Permission.InterventionsRead);
  }

  protected canDeactivate(): boolean {
    return this.permissionService.hasPermission(Permission.EmployeesDelete);
  }

  protected canActivate(): boolean {
    return this.permissionService.hasPermission(Permission.EmployeesUpdate);
  }

  protected saveEmployee(request: UpdateEmployeeRequest): void {
    if (!this.canUpdate()) {
      return;
    }

    const employeeId = this.employeeStore.selectedEmployee()?.id;

    if (!employeeId) {
      return;
    }

    this.employeeStore.updateEmployee(employeeId, request);
  }

  protected toggleEmployeeActive(employee: EmployeeResponse): void {
    if (employee.isActive) {
      if (!this.canDeactivate()) {
        return;
      }

      this.employeeStore.deactivateEmployee(employee.id);
      return;
    }

    if (!this.canActivate()) {
      return;
    }

    this.employeeStore.activateEmployee(employee.id);
  }

  protected onClientChanged(clientId: string | null): void {
    this.selectedClientId = clientId;
    this.clientStore.selectClient(clientId);

    this.employeeStore.loadCompanies(clientId);
    this.employeeStore.loadManagerLookupResponses();

    this.clientStore.filterClientCompanies(clientId, null);
  }

  protected onCompanyChanged(companyId: string | null): void {
    this.employeeStore.loadCompanyDetails(companyId);
    this.employeeStore.loadManagerLookupResponses();

    this.clientStore.filterClientCompanies(this.selectedClientId, companyId);
  }

  protected onManagerLookupSearchChanged(searchTerm: string): void {
    this.employeeStore.loadManagerLookupResponses(searchTerm);
  }

  protected openDependantsManagement(employee: EmployeeResponse): void {
    const dialogRef = this.dialog.open(EmployeeDependantsModalComponent, {
      width: '1000px',
      maxWidth: '95vw',
    });

    dialogRef.componentRef?.setInput('employeeId', employee.id);
    dialogRef.componentRef?.setInput('employeeDisplayName', `${employee.firstName} ${employee.surname}`.trim());
    dialogRef.componentRef?.setInput('canManage', this.canUpdate());
  }
}